import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '../../components/ui/Checkbox';
import { AiPromptDraft } from '../../queries/aiPrompts';

interface Props {
  draft: AiPromptDraft;
  onChange: (draft: AiPromptDraft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}

const TRACK_TYPES = ['all', 'metropolitan', 'provincial', 'country'];
const TRACK_CONDITIONS = ['all', 'firm', 'good', 'soft', 'heavy', 'synthetic'];
const DAYS = ['all', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'weekday', 'weekend'];

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';
// Instruction blocks carry a lot of prose, so give them real vertical room and
// a legible mono size (they were 70px/text-xs and felt cramped).
const textareaClass = `${inputClass} min-h-[150px] resize-y font-mono text-[13px] leading-relaxed`;

export function AiPromptFormDialog({ draft, onChange, onCancel, onSave, saving, error }: Props) {
  const isEdit = !!draft.id;
  const form = draft.form_weighting ?? 60;

  const set = (patch: AiPromptDraft) => onChange({ ...draft, ...patch });
  // Market weighting is always the complement of form weighting, so the
  // form_weighting + market_weighting = 100 DB constraint can never be violated.
  const setForm = (value: number) => set({ form_weighting: value, market_weighting: 100 - value });

  const field = (label: string, key: keyof AiPromptDraft, rows = true) => (
    <label className="col-span-2 block text-sm">
      {label}
      <textarea
        className={rows ? textareaClass : inputClass}
        value={(draft[key] as string) ?? ''}
        onChange={(e) => set({ [key]: e.target.value } as AiPromptDraft)}
      />
    </label>
  );

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{isEdit ? 'Edit prompt' : 'New prompt'}</Dialog.Title>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="block text-sm">
              Name
              <input className={inputClass} value={draft.prompt_name ?? ''} onChange={(e) => set({ prompt_name: e.target.value })} />
            </label>
            <label className="block text-sm">
              Version
              <input className={inputClass} value={draft.prompt_version ?? '1.0'} onChange={(e) => set({ prompt_version: e.target.value })} />
            </label>

            <label className="col-span-2 block text-sm">
              Description
              <input className={inputClass} value={draft.description ?? ''} onChange={(e) => set({ description: e.target.value })} />
            </label>

            <label className="block text-sm">
              Track type
              <select className={inputClass} value={draft.track_type ?? 'all'} onChange={(e) => set({ track_type: e.target.value })}>
                {TRACK_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Track condition
              <select className={inputClass} value={draft.track_condition ?? 'all'} onChange={(e) => set({ track_condition: e.target.value })}>
                {TRACK_CONDITIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Day of week
              <select className={inputClass} value={draft.day_of_week ?? 'all'} onChange={(e) => set({ day_of_week: e.target.value })}>
                {DAYS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Track name
              <input className={inputClass} value={draft.track_name ?? 'all'} onChange={(e) => set({ track_name: e.target.value || 'all' })} />
            </label>

            <label className="block text-sm">
              Form weighting: {form}%
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={form}
                onChange={(e) => setForm(Number(e.target.value))}
                className="mt-2 w-full"
              />
            </label>
            <div className="block text-sm">
              Market weighting
              <p className="mt-3 text-sm font-medium">{100 - form}% <span className="text-xs text-muted-foreground">(auto)</span></p>
            </div>

            <label className="block text-sm">
              Priority
              <input
                type="number"
                className={inputClass}
                value={draft.priority ?? 100}
                onChange={(e) => set({ priority: Number(e.target.value) })}
              />
            </label>

            <label className="col-span-2 block text-sm">
              System prompt
              <textarea className={textareaClass} value={draft.system_prompt ?? ''} onChange={(e) => set({ system_prompt: e.target.value })} />
            </label>

            {field('Analysis instructions', 'analysis_instructions')}
            {field('Market analysis instructions', 'market_analysis_instructions')}
            {field('Track-specific instructions', 'track_specific_instructions')}
            {field('Condition-specific instructions', 'condition_specific_instructions')}
            {field('Day-specific instructions', 'day_specific_instructions')}
            {field('Output format instructions', 'output_format_instructions')}

            <div className="col-span-2 flex flex-wrap items-center gap-4 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={draft.is_active !== false} onChange={(e) => set({ is_active: e.target.checked })} />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!draft.is_default} onChange={(e) => set({ is_default: e.target.checked })} />
                Default
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!draft.include_historical_strike_rate}
                  onChange={(e) => set({ include_historical_strike_rate: e.target.checked })}
                />
                Include historical strike rate
              </label>
            </div>

            {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onCancel} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
